import { getOperatorDisplayName } from 'dashboard/lib/workflows/conditional';
import {
  JsonLogicField,
  JsonLogicValue,
  RuleValueConfigField,
  RuleValueConfigValue,
  RuleValueTypes,
} from 'dashboard/lib/workflows/json-logic';
import { ValueTransformer } from 'dashboard/lib/workflows/objects';
import { WorkflowOperatorKey } from 'dashboard/models/workflow';
import { VariableType } from 'dashboard/models/workflow-version';
import { JsonSchemaBasicTypes, JsonSchemaTypes } from 'lib/json-schema';

export const DEFAULT_EXPRESSION: ExpressionRule = { [WorkflowOperatorKey.Add]: [] };

export type ExpressionRule = Partial<Record<WorkflowOperatorKey, ExpressionRuleRightHand>>;
export type ExpressionRuleRightHand = [ExpressionRuleValueAll?, ExpressionRuleValueAll?, number?];
export type ExpressionVar = { logicvar: string };
export type ExpressionRuleValueAll =
  | JsonLogicValue
  | JsonLogicField
  | ExpressionVar
  | ExpressionRule;

export type ExpressionRuleValueConfigRule = {
  type: RuleValueTypes.Rule;
  value: ExpressionRule;
  paths: string[];
};
export type ExpressionRuleValueConfigVariable = {
  type: RuleValueTypes.Variable;
  value: ExpressionVar;
  paths: string[];
};
export type ExpressionRuleValueConfig =
  | ExpressionRuleValueConfigRule
  | ExpressionRuleValueConfigVariable
  | RuleValueConfigField
  | RuleValueConfigValue;

export const SINGLE_OPERAND_OPERATORS = [
  WorkflowOperatorKey.Sum,
  WorkflowOperatorKey.Avg,
  WorkflowOperatorKey.IsBlank,
  WorkflowOperatorKey.IsNotBlank,
];

export const getExpressionRuleValueConfig = (
  ruleValue: ExpressionRuleValueAll,
): ExpressionRuleValueConfig => {
  if (ruleValue == null) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return null;
  }

  if (typeof ruleValue === 'object') {
    const key = Object.keys(ruleValue)[0];
    if (key === 'var') {
      const value = ruleValue as JsonLogicField;
      return {
        type: RuleValueTypes.Field,
        value,
        paths: [value.var],
      };
    }

    if (key === 'logicvar') {
      const value = ruleValue as ExpressionVar;
      return {
        type: RuleValueTypes.Variable,
        value,
        paths: [value.logicvar],
      };
    }

    if (
      [
        WorkflowOperatorKey.Add,
        WorkflowOperatorKey.Subtract,
        WorkflowOperatorKey.Multiply,
        WorkflowOperatorKey.Divide,
        WorkflowOperatorKey.Reduce,
        WorkflowOperatorKey.OrAssign,
        WorkflowOperatorKey.Equals,
        WorkflowOperatorKey.DoesNotEqual,
        WorkflowOperatorKey.GreaterThan,
        WorkflowOperatorKey.GreaterThanOrEqual,
        WorkflowOperatorKey.LessThan,
        WorkflowOperatorKey.LessThanOrEqual,
        WorkflowOperatorKey.IsBlank,
        WorkflowOperatorKey.IsNotBlank,
        WorkflowOperatorKey.StartsWith,
        WorkflowOperatorKey.And,
        WorkflowOperatorKey.Or,
      ].includes(key as WorkflowOperatorKey)
    ) {
      return {
        type: RuleValueTypes.Rule,
        value: ruleValue as ExpressionRule,
        paths: [],
      };
    }
  }

  return {
    type: RuleValueTypes.Value,
    value: ruleValue as JsonLogicValue,
    paths: [],
  };
};

export type ExpresionRuleParts = {
  operator: WorkflowOperatorKey;
  valueLeftConfig: ExpressionRuleValueConfig;
  valueRightConfig?: ExpressionRuleValueConfig;
  valueDefaultConfig?: ExpressionRuleValueConfig;
};

export const getExpressionRuleParts = (rule: ExpressionRule): ExpresionRuleParts => {
  const operator = Object.keys(rule)[0] as WorkflowOperatorKey;
  const values = rule[operator];

  if (!operator || !values?.length) {
    return {
      operator: operator || null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      valueLeftConfig: null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      valueRightConfig: null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      valueDefaultConfig: null,
    };
  }

  if (operator === WorkflowOperatorKey.OrAssign && values?.length) {
    const computation = getExpressionRuleParts(values[0] as ExpressionRule);
    if (values[1] != null && !Number.isNaN(values[1])) {
      computation.valueDefaultConfig = getExpressionRuleValueConfig(values[1]);
    }
    return computation;
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const valueLeftConfig = getExpressionRuleValueConfig(values[0]);
  if (operator === WorkflowOperatorKey.Reduce) {
    const innerRule = getExpressionRuleParts(values[1] as ExpressionRule);

    if (innerRule.operator === WorkflowOperatorKey.Add) {
      return {
        operator: WorkflowOperatorKey.Sum,
        valueLeftConfig: valueLeftConfig as RuleValueConfigField,
      };
    }
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const valueRightConfig = values.length > 1 ? getExpressionRuleValueConfig(values[1]) : null;
  if (operator === WorkflowOperatorKey.Divide && valueLeftConfig?.type === RuleValueTypes.Rule) {
    const nestedRuleParts = getExpressionRuleParts(valueLeftConfig.value as ExpressionRule);

    if (nestedRuleParts.operator === WorkflowOperatorKey.Sum) {
      const nestedLeftFieldPath = nestedRuleParts.valueLeftConfig.paths[0];

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (valueRightConfig.paths[0] === `${nestedLeftFieldPath}.length`) {
        return {
          operator: WorkflowOperatorKey.Avg,
          valueLeftConfig: nestedRuleParts.valueLeftConfig as RuleValueConfigField,
        };
      }
    }
  }

  return {
    operator,
    valueLeftConfig,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    valueRightConfig,
  };
};

export const RULE_VALUE_TYPE_OPTIONS = [
  RuleValueTypes.Field,
  RuleValueTypes.Value,
  RuleValueTypes.Variable,
].map((valueType) => ({
  value: valueType,
  label: valueType,
}));

export const VARIABLE_TYPE_TO_OPERATORS: Record<VariableType, WorkflowOperatorKey[]> = {
  [JsonSchemaTypes.Number]: [
    WorkflowOperatorKey.Add,
    WorkflowOperatorKey.Subtract,
    WorkflowOperatorKey.Multiply,
    WorkflowOperatorKey.Divide,
    WorkflowOperatorKey.Sum,
    WorkflowOperatorKey.Avg,
  ],
  [JsonSchemaTypes.String]: [WorkflowOperatorKey.Concat],
  [JsonSchemaTypes.Boolean]: [
    WorkflowOperatorKey.Equals, // 2 number or string or boolean inputs
    WorkflowOperatorKey.DoesNotEqual, // 2 number or string or boolean inputs
    WorkflowOperatorKey.GreaterThan, // 2 number inputs
    WorkflowOperatorKey.GreaterThanOrEqual, // 2 number inputs
    WorkflowOperatorKey.LessThan, // 2 number inputs
    WorkflowOperatorKey.LessThanOrEqual, // 2 number inputs
    WorkflowOperatorKey.IsBlank, // 1 string input
    WorkflowOperatorKey.IsNotBlank, // 1 string input
    WorkflowOperatorKey.StartsWith, // 2 string inputs
    WorkflowOperatorKey.And, // 2 boolean inputs
    WorkflowOperatorKey.Or, // 2 boolean inputs
  ],
};

export const getVariableAllowedTypes = (type: VariableType, operator: WorkflowOperatorKey) => {
  switch (operator) {
    case WorkflowOperatorKey.Add:
    case WorkflowOperatorKey.Subtract:
    case WorkflowOperatorKey.Multiply:
    case WorkflowOperatorKey.Divide:
    case WorkflowOperatorKey.GreaterThan:
    case WorkflowOperatorKey.GreaterThanOrEqual:
    case WorkflowOperatorKey.LessThan:
    case WorkflowOperatorKey.LessThanOrEqual:
      return [JsonSchemaTypes.Number, JsonSchemaTypes.Integer];
    case WorkflowOperatorKey.Concat:
    case WorkflowOperatorKey.IsBlank:
    case WorkflowOperatorKey.IsNotBlank:
    case WorkflowOperatorKey.StartsWith:
      return [JsonSchemaTypes.String];
    case WorkflowOperatorKey.Equals:
    case WorkflowOperatorKey.DoesNotEqual:
      return [
        JsonSchemaTypes.String,
        JsonSchemaTypes.Number,
        JsonSchemaTypes.Integer,
        JsonSchemaTypes.Boolean,
      ];
    case WorkflowOperatorKey.And:
    case WorkflowOperatorKey.Or:
      return [JsonSchemaTypes.Boolean];
    case WorkflowOperatorKey.OrAssign:
      return [type];
    case WorkflowOperatorKey.Sum:
    case WorkflowOperatorKey.Avg:
      // array operators
      return [JsonSchemaTypes.Array];
  }
};

export const getVariableAllowedArrayTypes = (
  operator: WorkflowOperatorKey,
): (JsonSchemaBasicTypes | JsonSchemaTypes.Object)[] => {
  switch (operator) {
    case WorkflowOperatorKey.Sum:
    case WorkflowOperatorKey.Avg:
      return [JsonSchemaTypes.Number, JsonSchemaTypes.Integer];
    default:
      return [];
  }
};

export const getOperatorOptions = (type: VariableType) => {
  return VARIABLE_TYPE_TO_OPERATORS[type].map((operator) => ({
    label: getOperatorDisplayName(operator),
    value: operator,
  }));
};

export const generateArrayRule = (
  operator: WorkflowOperatorKey,
  arrayVar: JsonLogicField | ExpressionVar,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
): ExpressionRule => {
  if (operator === WorkflowOperatorKey.Sum) {
    return {
      [WorkflowOperatorKey.Reduce]: [
        arrayVar,
        {
          [WorkflowOperatorKey.Add]: [{ var: 'current' }, { var: 'accumulator' }],
        },
        0,
      ],
    };
  }

  if (operator === WorkflowOperatorKey.Avg) {
    const sumRule = generateArrayRule(WorkflowOperatorKey.Sum, arrayVar);
    const [varKey, arrayPath] = Object.entries(arrayVar)[0];
    return {
      [WorkflowOperatorKey.Divide]: [sumRule, { [varKey]: `${arrayPath}.length` }],
    };
  }
};

export const generateExpressionRule = (
  expression: ExpressionRule,
  defaultValue: ExpressionRuleValueAll,
): ExpressionRule => {
  if (defaultValue == null || Number.isNaN(defaultValue)) {
    // false, 0, "" are acceptable as default values
    return expression;
  }
  return {
    [WorkflowOperatorKey.OrAssign]: [expression, defaultValue],
  };
};

const getDisplayTerm = (valueConfig: ExpressionRuleValueConfig, vt: ValueTransformer) => {
  if (!valueConfig) {
    return '___';
  }

  const { type, value, paths } = valueConfig;
  switch (type) {
    case RuleValueTypes.Rule:
      return getVariableDisplay(value, vt);
    case RuleValueTypes.Field:
    case RuleValueTypes.Variable:
      return vt.getDisplayValue(paths[0]);
    case RuleValueTypes.Value:
      return typeof value === 'string' ? `"${value}"` : value;
  }
};

export const getVariableDisplay = (expression: ExpressionRule, vt: ValueTransformer): string => {
  const { operator, valueLeftConfig, valueRightConfig, valueDefaultConfig } =
    getExpressionRuleParts(expression);
  const defaultDisplay = valueDefaultConfig
    ? ` (or ${getDisplayTerm(valueDefaultConfig, vt)})`
    : ``;

  switch (operator) {
    case WorkflowOperatorKey.Add:
    case WorkflowOperatorKey.Multiply:
    case WorkflowOperatorKey.Divide:
    case WorkflowOperatorKey.StartsWith:
    case WorkflowOperatorKey.And:
    case WorkflowOperatorKey.Or: {
      return `${getDisplayTerm(valueLeftConfig, vt)} ${operator} ${getDisplayTerm(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        valueRightConfig,
        vt,
      )}${defaultDisplay}`;
    }
    case WorkflowOperatorKey.Subtract: {
      return `${getDisplayTerm(valueLeftConfig, vt)} - ${getDisplayTerm(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        valueRightConfig,
        vt,
      )}${defaultDisplay}`;
    }
    case WorkflowOperatorKey.Concat: {
      return `${getDisplayTerm(valueLeftConfig, vt)} ${WorkflowOperatorKey.Add} ${getDisplayTerm(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        valueRightConfig,
        vt,
      )}`;
    }
    case WorkflowOperatorKey.Equals:
    case WorkflowOperatorKey.DoesNotEqual: {
      // Use string so we don't display things like a = b = c.
      return `${getDisplayTerm(valueLeftConfig, vt)} ${getOperatorDisplayName(
        operator,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      )} ${getDisplayTerm(valueRightConfig, vt)}${defaultDisplay}`;
    }
    case WorkflowOperatorKey.GreaterThan:
    case WorkflowOperatorKey.GreaterThanOrEqual:
    case WorkflowOperatorKey.LessThan:
    case WorkflowOperatorKey.LessThanOrEqual: {
      // These look weird without parenthesis.
      return `(${getDisplayTerm(valueLeftConfig, vt)} ${operator} ${getDisplayTerm(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        valueRightConfig,
        vt,
      )})${defaultDisplay}`;
    }

    case WorkflowOperatorKey.IsBlank:
    case WorkflowOperatorKey.IsNotBlank: {
      return `${getDisplayTerm(valueLeftConfig, vt)} ${operator}${defaultDisplay}`;
    }
    case WorkflowOperatorKey.Avg:
    case WorkflowOperatorKey.Sum:
      return `${operator}(${getDisplayTerm(valueLeftConfig, vt)})${defaultDisplay}`;
    default:
      return '';
  }
};
